/**
 * EmailDetailDialog Component
 * 
 * Shows full email details in a dialog.
 */

import { Mail, Briefcase, Building2, Calendar, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { OutreachEmail, JobListing, CompanyContact } from '@/types/job-hunt';

interface EmailDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: OutreachEmail;
  jobListing?: JobListing;
  contact?: CompanyContact;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  scheduled: { label: 'Scheduled', variant: 'default' },
  sent: { label: 'Sent', variant: 'outline' },
};

export function EmailDetailDialog({
  open,
  onOpenChange,
  email,
  jobListing,
  contact,
}: EmailDetailDialogProps) {
  const config = statusConfig[email.status] || statusConfig.draft;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <DialogTitle className="text-header font-semantic-bold">
              Email Details
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status and Purpose */}
          <div className="flex gap-2">
            <Badge variant={config.variant}>{config.label}</Badge>
            <Badge variant="outline">{email.purpose}</Badge>
            <Badge variant="outline">Tone: {email.tone}</Badge>
          </div>

          {/* Subject */}
          <div>
            <h3 className="text-title font-semantic-semibold mb-2">Subject</h3>
            <p className="text-body">{email.subject}</p>
          </div>

          <Separator />

          {/* Body */}
          <div>
            <h3 className="text-title font-semantic-semibold mb-2">Message</h3>
            <div className="text-body whitespace-pre-wrap bg-muted/50 p-4 rounded-lg">
              {email.body}
            </div>
          </div>

          <Separator />

          {/* Associated Information */}
          <div className="space-y-3">
            <h3 className="text-title font-semantic-semibold">Associated With</h3>
            
            {jobListing && (
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Briefcase className="h-4 w-4 text-muted-foreground mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-body font-medium">{jobListing.title}</p>
                  <p className="text-fine text-muted-foreground">
                    {jobListing.company_name} • {jobListing.location || 'Remote'}
                  </p>
                </div>
              </div>
            )}

            {contact && (
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <User className="h-4 w-4 text-muted-foreground mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-body font-medium">{contact.contact_name}</p>
                  <p className="text-fine text-muted-foreground">
                    {contact.contact_title && `${contact.contact_title} • `}
                    {contact.company_name}
                  </p>
                  {contact.contact_email && (
                    <p className="text-fine text-muted-foreground">{contact.contact_email}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Timeline */}
          <div className="space-y-2">
            <h3 className="text-title font-semantic-semibold">Timeline</h3>
            <div className="space-y-2 text-fine text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-3 w-3" />
                <span>Created: {new Date(email.created_at).toLocaleString()}</span>
              </div>
              {email.scheduled_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <span>Scheduled: {new Date(email.scheduled_date).toLocaleString()}</span>
                </div>
              )}
              {email.sent_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <span>Sent: {new Date(email.sent_date).toLocaleString()}</span>
                </div>
              )}
              {email.reply_received_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <span>Reply Received: {new Date(email.reply_received_date).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
